/**
 * BoxTruckLoadRouteMiniMap
 *
 * The real route, drawn inside the load drawer at the placeholder's size — what an operator who turned on
 * "Open map when a load is opened" sees instead of `BoxTruckRouteTeaser`.
 *
 * It is the SAME map the planner modal draws (`BoxTruckLoadMapEditorMap`), not a second implementation: one
 * Directions call, the shared Numeo pins, the same geocode fallback for a load whose coordinates the feed
 * did not carry. What differs is that nothing here is editable — the pins do not drag, because the drawer
 * has no route to save and moving one would promise an edit nothing reads. Expand still opens the planner,
 * where points ARE moved.
 *
 * This costs what the placeholder was introduced to stop paying (#12350): a geocode per unlocated stop and a
 * Directions request, on every load opened. That is why it renders only when the operator asked for it — see
 * `BoxTruckBoardPreferences.openMapOnExpand`.
 */

import { useEffect, useRef, useState } from 'react'
import { Maximize2 } from 'lucide-react'
import { CanonicalLoad } from '@numeo/types-core'
import GoogleMapsLoader from '@/components/GoogleMapsLoader'
import { geocodeCityState } from '@/components/geocode'
import { Spinner } from '@/components/ds2'
import { cityLabel } from '../utils/format'
import { trackBoxTruckUiInteraction } from '../utils/analytics'
import { BoxTruckLoadMapEditorMap } from './BoxTruckLoadMapEditorMap'
import { LatLng, toLatLng } from './NearbyDriverRouteMap'

const noop = () => {}

/** Fires once per resolved load, from an effect, so a render cannot double-count it. */
const DrawnOnce = ({ onDraw }: { onDraw: () => void }) => {
    const firedRef = useRef(false)
    useEffect(() => {
        if (firedRef.current) return
        firedRef.current = true
        onDraw()
    }, [onDraw])
    return null
}

/**
 * Module scope on purpose. The editable map rebuilds its markers and asks Directions for a fresh route
 * whenever its `waypoints` identity changes, so an inline `[]` would have billed a route on every render of
 * the drawer — a bid arriving, an annotation saved, the board refreshing — for a load whose two ends never
 * moved. The mini map has no waypoints at all; it only has to say so with the same array each time.
 */
const NO_WAYPOINTS: LatLng[] = []

export const BoxTruckLoadRouteMiniMap = ({ load, onExpand }: { load: CanonicalLoad; onExpand: () => void }) => {
    const [ends, setEnds] = useState<{ origin: LatLng; destination: LatLng } | null>(null)
    const [resolving, setResolving] = useState(true)

    // The two ENDS, as values. Keying this on the `load` object would re-resolve on every board refresh and
    // every socket update: the drawer's load is re-derived from the visible rows, so the same posting
    // arrives as a new object, and the effect would blank the map, geocode again and pay for another route
    // — a visible flicker on a drawer nobody touched. These eight fields are what the route is made of.
    const { pickupCity, pickupStateProv, pickupLat, pickupLon, deliveryCity, deliveryStateProv, deliveryLat, deliveryLon } = load

    useEffect(() => {
        // PER RUN, not a ref shared by all of them. The drawer does not remount this component when the
        // operator switches loads — it hands down a new load and this effect runs again — and a shared flag
        // is set back to true by the new run before the previous one's geocode has answered. The old run's
        // guard then passes and writes ITS ends: pick a load whose cities need the full geocode fallback,
        // switch to one that carries coordinates, and the second load's drawer draws the first load's route.
        // On a board scanned in seconds that is an operator reading the wrong lane.
        let cancelled = false
        setResolving(true)
        setEnds(null)
        const locate = async (city: string | null, state: string | null, lat: number | null | undefined, lon: number | null | undefined) =>
            toLatLng(lat, lon) ?? (await geocodeCityState(city, state))
        const resolve = async () => {
            const [origin, destination] = await Promise.all([
                locate(pickupCity, pickupStateProv, pickupLat, pickupLon),
                locate(deliveryCity, deliveryStateProv, deliveryLat, deliveryLon),
            ])
            if (cancelled) return
            setEnds(origin && destination ? { origin, destination } : null)
            setResolving(false)
        }
        void resolve()
        return () => {
            cancelled = true
        }
    }, [pickupCity, pickupStateProv, pickupLat, pickupLon, deliveryCity, deliveryStateProv, deliveryLat, deliveryLon])

    return (
        <div className="relative h-44 w-full overflow-hidden rounded-xl border border-border">
            {resolving ? (
                <div className="flex h-full items-center justify-center gap-2 text-sm text-foreground-muted">
                    <Spinner color="accent" size={18} /> Drawing the route…
                </div>
            ) : ends ? (
                <GoogleMapsLoader>
                    {/* Counted where the planner's own open is counted. Without this, moving the map onto the
                        open path would have made map usage LOOK like it collapsed: `map_opened` only ever
                        meant "Expand was pressed". */}
                    <DrawnOnce onDraw={() => trackBoxTruckUiInteraction('map_drawn_on_open')} />
                    <BoxTruckLoadMapEditorMap
                        origin={ends.origin}
                        destination={ends.destination}
                        waypoints={NO_WAYPOINTS}
                        editable={false}
                        onOriginChange={noop}
                        onDestinationChange={noop}
                        onWaypointChange={noop}
                    />
                </GoogleMapsLoader>
            ) : (
                // Said plainly rather than shown as an empty map: this load's cities did not resolve to
                // coordinates, and the planner cannot do better with the same two names.
                <div className="flex h-full items-center justify-center px-4 text-center text-sm text-foreground-muted">
                    Couldn’t place {cityLabel(load.pickupCity, load.pickupStateProv)} → {cityLabel(load.deliveryCity, load.deliveryStateProv)} on a map.
                </div>
            )}

            <button
                type="button"
                onClick={onExpand}
                aria-label="Open the route planner"
                className="absolute right-2 top-2 inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs font-semibold text-foreground shadow-overlay transition-colors hover:bg-default"
            >
                <Maximize2 size={13} strokeWidth={2} /> Expand
            </button>
        </div>
    )
}
